// React-контекст поверх reducer.ts + персист в storage. Не импортирует src/core.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type Dispatch,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { AppState, Currency, CurrencyDef, Segment } from '../types';
import { type Actions, type StoreAction, newId, reducer } from './reducer';
import { parseStateJson, serializeState, STORAGE_KEY } from './persist';
import { seedState } from './seed';
import { createStorage, type StorageAdapter } from './storage';

const AUTOSAVE_DEBOUNCE_MS = 250;

const AppStateContext = createContext<AppState | undefined>(undefined);
const ActionsContext = createContext<Actions | undefined>(undefined);

function loadInitialState(storage: StorageAdapter): AppState {
  const raw = storage.get(STORAGE_KEY);
  if (raw === null || raw.length === 0) {
    return seedState();
  }
  const result = parseStateJson(raw);
  if (result.ok) {
    return result.state;
  }
  return seedState();
}

/**
 * Отметка «когда блок попал в библиотеку» ставится здесь, на границе
 * императивного слоя: reducer остаётся чистым (Date.now() внутри него сделал
 * бы результат невоспроизводимым), а ни одна форма не может забыть проставить
 * метку у себя — путей добавления блока несколько.
 *
 * Уже проставленную метку не трогаем: разбор источника знает про происхождение
 * блока больше, чем стор, и ставит origin сам. Всё остальное — ручное
 * создание, поэтому 'manual' здесь и есть значение по умолчанию.
 */
export function stampAdded(seg: Segment): Segment {
  if (seg.addedAt !== undefined) return seg;
  return { ...seg, addedAt: Date.now(), origin: seg.origin ?? 'manual' };
}

function buildActions(dispatch: Dispatch<StoreAction>): Actions {
  return {
    addSegment(seg: Segment): void {
      dispatch({ type: 'addSegment', seg: stampAdded(seg) });
    },
    addSegments(segs: Segment[]): void {
      dispatch({ type: 'addSegments', segs: segs.map(stampAdded) });
    },
    updateSegment(id: string, patch: Partial<Segment>): void {
      dispatch({ type: 'updateSegment', id, patch });
    },
    deleteSegment(id: string): void {
      dispatch({ type: 'deleteSegment', id });
    },
    addVariant(name?: string): void {
      dispatch({ type: 'addVariant', name });
    },
    renameVariant(id: string, name: string): void {
      dispatch({ type: 'renameVariant', id, name });
    },
    deleteVariant(id: string): void {
      dispatch({ type: 'deleteVariant', id });
    },
    setVariantColor(id: string, color: string): void {
      dispatch({ type: 'setVariantColor', id, color });
    },
    duplicateVariant(id: string, name?: string): void {
      dispatch({ type: 'duplicateVariant', id, name });
    },
    moveVariant(from: number, to: number): void {
      dispatch({ type: 'moveVariant', from, to });
    },
    setVariantLocked(id: string, locked: boolean): void {
      dispatch({ type: 'setVariantLocked', id, locked });
    },
    setVariantCollapsed(id: string, collapsed: boolean): void {
      dispatch({ type: 'setVariantCollapsed', id, collapsed });
    },
    addToVariant(variantId: string, segmentId: string, index?: number): void {
      dispatch({ type: 'addToVariant', variantId, segmentId, index });
    },
    removeFromVariant(variantId: string, segmentId: string): void {
      dispatch({ type: 'removeFromVariant', variantId, segmentId });
    },
    moveInVariant(variantId: string, from: number, to: number): void {
      dispatch({ type: 'moveInVariant', variantId, from, to });
    },
    setTravelers(n: number): void {
      dispatch({ type: 'setTravelers', n });
    },
    setDisplayCurrency(c: Currency): void {
      dispatch({ type: 'setDisplayCurrency', c });
    },
    addCurrency(def: CurrencyDef): void {
      dispatch({ type: 'addCurrency', def });
    },
    updateCurrency(code: Currency, patch: Partial<Omit<CurrencyDef, 'code'>>): void {
      dispatch({ type: 'updateCurrency', code, patch });
    },
    removeCurrency(code: Currency): void {
      dispatch({ type: 'removeCurrency', code });
    },
    replaceState(s: AppState): void {
      dispatch({ type: 'replaceState', state: s });
    },
    resetToSeed(): void {
      dispatch({ type: 'resetToSeed' });
    },
  };
}

export function StoreProvider(props: { children: ReactNode }): ReactElement {
  const [storage] = useState<StorageAdapter>(() => createStorage());
  const [state, dispatch] = useReducer(reducer, undefined as unknown as AppState, () =>
    loadInitialState(storage),
  );
  const actions = useMemo<Actions>(() => buildActions(dispatch), [dispatch]);

  useEffect(() => {
    const serialized = serializeState(state);
    const timer = setTimeout(() => {
      storage.set(STORAGE_KEY, serialized);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [state, storage]);

  return (
    <AppStateContext.Provider value={state}>
      <ActionsContext.Provider value={actions}>{props.children}</ActionsContext.Provider>
    </AppStateContext.Provider>
  );
}

export function useStore(): AppState {
  const ctx = useContext(AppStateContext);
  if (ctx === undefined) {
    throw new Error('useStore() должен использоваться внутри <StoreProvider>');
  }
  return ctx;
}

export function useActions(): Actions {
  const ctx = useContext(ActionsContext);
  if (ctx === undefined) {
    throw new Error('useActions() должен использоваться внутри <StoreProvider>');
  }
  return ctx;
}

export function useSegment(id: string): Segment | undefined {
  const state = useStore();
  return state.library[id];
}

export { newId };
